import axios from 'axios'

const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL || ''}/api`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

// Attach admin token to requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('adminToken')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Handle 401 → redirect to login
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      sessionStorage.removeItem('adminToken')
      window.location.href = '/login'
    }
    return Promise.reject(err)
  },
)

// ── User / Auth ─────────────────────────────────────────────────
export const getMe = () => api.get('/me')

const authApi = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth`,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})
export const requestMagicLink  = (email) => authApi.post('/magic-link', { email })
export const verifyMagicToken  = (token) => authApi.post('/magic-link/verify', { token })

// ── Dashboard ────────────────────────────────────────────────────
export const getDashboard = () => api.get('/dashboard')

// ── Invoices ─────────────────────────────────────────────────────
export const getInvoices = (params) => api.get('/invoices', { params })
export const getInvoice = (id) => api.get(`/invoices/${id}`)
export const createInvoice = (data) => api.post('/invoices', data)
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data)
export const approveInvoice = (id, body) => api.post(`/invoices/${id}/approve`, body)
export const rejectInvoice = (id, notes) => api.post(`/invoices/${id}/reject`, { notes })
export const sendInvoice = (id) => api.post(`/invoices/${id}/send`)
export const revertInvoice = (id, reason) => api.post(`/invoices/${id}/revert`, { reason })
export const cancelInvoice = (id, reason) => api.post(`/invoices/${id}/cancel`, { reason })
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`)
export const downloadInvoicePdf = (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' })
export const roundOffInvoice = (id, data) => api.post(`/invoices/${id}/round-off`, data)
// Undo a round-off. Round Off pre-fills the whole remaining balance, so it is
// the de facto "mark as paid"; this is the way back when a real receipt for
// that balance turns up afterwards. Admin only, reason mandatory.
export const reverseInvoiceAdjustment = (invoiceId, adjustmentId, data) =>
  api.post(`/invoices/${invoiceId}/adjustments/${adjustmentId}/reverse`, data)

// ── Estimates ───────────────────────────────────────────────────
export const getEstimates = (params) => api.get('/estimates', { params })
export const getEstimate = (id) => api.get(`/estimates/${id}`)
export const createEstimate = (data) => api.post('/estimates', data)
export const updateEstimate = (id, data) => api.put(`/estimates/${id}`, data)
export const approveEstimate = (id) => api.post(`/estimates/${id}/approve`)
export const sendEstimate = (id) => api.post(`/estimates/${id}/send`)
export const revertEstimate = (id, reason) => api.post(`/estimates/${id}/revert`, { reason })
export const acceptEstimate = (id) => api.post(`/estimates/${id}/accept`)
export const declineEstimate = (id, reason) => api.post(`/estimates/${id}/decline`, { reason })
export const convertEstimateToInvoice = (id) => api.post(`/estimates/${id}/convert`)
export const deleteEstimate = (id) => api.delete(`/estimates/${id}`)
export const downloadEstimatePdf = (id) => api.get(`/estimates/${id}/pdf`, { responseType: 'blob' })

// ── Delivery Challans ───────────────────────────────────────────
export const getDeliveryChallans = (params) => api.get('/delivery-challans', { params })
export const getDeliveryChallan = (id) => api.get(`/delivery-challans/${id}`)
export const createDeliveryChallan = (data) => api.post('/delivery-challans', data)
export const updateDeliveryChallan = (id, data) => api.put(`/delivery-challans/${id}`, data)
export const approveDeliveryChallan = (id) => api.post(`/delivery-challans/${id}/approve`)
export const revertDeliveryChallan = (id) => api.post(`/delivery-challans/${id}/revert`)
export const dispatchDeliveryChallan = (id, data) => api.post(`/delivery-challans/${id}/dispatch`, data)
export const deliverDeliveryChallan = (id, data) => api.post(`/delivery-challans/${id}/deliver`, data)
export const deleteDeliveryChallan = (id) => api.delete(`/delivery-challans/${id}`)
export const downloadDeliveryChallanPdf = (id) => api.get(`/delivery-challans/${id}/pdf`, { responseType: 'blob' })

// ── Credit Notes ─────────────────────────────────────────────────
export const getCreditNotes = (params) => api.get('/credit-notes', { params })
export const getCreditNote = (id) => api.get(`/credit-notes/${id}`)
export const createCreditNote = (data) => api.post('/credit-notes', data)
export const updateCreditNote = (id, data) => api.put(`/credit-notes/${id}`, data)
export const approveCreditNote = (id) => api.post(`/credit-notes/${id}/approve`)

// ── Customers ────────────────────────────────────────────────────
export const getCustomers = (params) => api.get('/customers', { params })
export const getCustomer = (id) => api.get(`/customers/${id}`)
export const createCustomer = (data) => api.post('/customers', data)
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data)

// ── Products/Services ────────────────────────────────────────────
export const getProducts = (params) => api.get('/products', { params })
export const getProduct = (id) => api.get(`/products/${id}`)
export const createProduct = (data) => api.post('/products', data)
export const updateProduct = (id, data) => api.put(`/products/${id}`, data)

// ── Payments ─────────────────────────────────────────────────────
export const getPayments = (params) => api.get('/payments', { params })
export const recordPayment = (data) => api.post('/payments', data)
// Reverse a payment recorded in error: deletes it, gives the invoice its
// balance and status back, and returns any bank line it came from to review.
// Admin only, reason mandatory.
export const voidPayment = (id, data) => api.post(`/payments/${id}/void`, data)
export const generateRazorpayLink = (invoiceId) => api.post(`/payments/${invoiceId}/razorpay`)

// ── Expenses ─────────────────────────────────────────────────────
export const getExpenses = (params) => api.get('/expenses', { params })
export const getExpense = (id) => api.get(`/expenses/${id}`)
export const createExpense = (data) => api.post('/expenses', data)
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data)
export const approveExpense = (id) => api.post(`/expenses/${id}/approve`)
export const rejectExpense = (id, notes) => api.post(`/expenses/${id}/reject`, { notes })
export const reimburseExpense = (id) => api.post(`/expenses/${id}/reimburse`)
export const uploadReceipt = (id, file) => {
  const formData = new FormData()
  formData.append('receipt', file)
  return api.post(`/expenses/${id}/receipt`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// ── Reports ──────────────────────────────────────────────────────
export const getGSTR1Report = (params) => api.get('/reports/gstr1', { params })
export const getReceivablesReport = (params) => api.get('/reports/receivables', { params })
export const getRevenueReport = (params) => api.get('/reports/revenue', { params })
export const getExpenseReport = (params) => api.get('/reports/expenses', { params })

// ── Exports ──────────────────────────────────────────────────────
export const exportTally = (params) => api.get('/exports/tally', { params, responseType: 'blob' })
export const exportCsv = (params) => api.get('/exports/csv', { params, responseType: 'blob' })
export const getMonthlyExport = (month) => api.get('/exports/monthly', { params: { month } })
export const getFullBackup = () => api.get('/exports/full-backup')

// ── Bank Reconciliation ─────────────────────────────────────────
export const reconcileBank = (transactions) => api.post('/bank/reconcile', { transactions })
export const confirmBankPayment = (data) => api.post('/bank/confirm', data)

// Statement import + annotation. The statement is parsed server-side and stored,
// so a part-finished review survives a reload and re-uploading is harmless.
export const importBankStatement = (file, since) => {
  const form = new FormData()
  form.append('statement', file)
  if (since) form.append('since', since)
  return api.post('/bank/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const getBankTransactions = (params) => api.get('/bank/transactions', { params })
export const updateBankTransaction = (id, data) => api.patch(`/bank/transactions/${id}`, data)
export const approveBankTransaction = (id, data) => api.post(`/bank/transactions/${id}/approve`, data)
export const bulkApproveBankTransactions = (data) => api.post('/bank/bulk-approve', data)
// A whole review pass in one request. Selections are staged in the page and sent
// together, so a pass costs one round trip rather than two per row.
export const saveBankBatch = (items) => api.post('/bank/transactions/batch', { items })
// Split one credit across several invoices. The single-invoice control cannot
// express the answer for bulk settlements — 28 of 32 stuck credits have no
// invoice at their amount at all.
export const allocateBankCredit = (id, allocations) => api.post(`/bank/transactions/${id}/allocate`, { allocations })
// Reverse a recorded credit: deletes the payments that line created, restores
// each invoice's balance and status, returns the line to review. Admin only.
// Timeout matches the server-side transaction, not the 30s axios default.
export const undoBankTransaction = (id) => api.post(`/bank/transactions/${id}/undo`, {}, { timeout: 120000 })
export const downloadBankStatementCsv = (params) => api.get('/bank/export/statement.csv', { params, responseType: 'blob' })
export const publishBankStatementSheet = (data) => api.post('/bank/export/sheet', data)

// ── Settings ─────────────────────────────────────────────────────
export const getCompanySettings = () => api.get('/settings/company')
export const updateCompanySettings = (data) => api.put('/settings/company', data)
export const getTeamMembers = () => api.get('/settings/team')
export const addTeamMember = (data) => api.post('/settings/team', data)
export const updateTeamMember = (id, data) => api.put(`/settings/team/${id}`, data)
export const getExpenseCategories = () => api.get('/settings/categories')
export const addExpenseCategory = (data) => api.post('/settings/categories', data)
export const updateExpenseCategory = (id, data) => api.put(`/settings/categories/${id}`, data)

// ── Google Drive integration ────────────────────────────────────
export const getDriveStatus = () => api.get('/drive/status')
// Browser-redirect endpoint (unprotected) — navigate the window here to start consent.
export const driveConnectUrl = () => `${import.meta.env.VITE_API_BASE_URL || ''}/api/drive/connect`

export default api
